import { IElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { Html } from "@react-three/drei";
import { ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, type JSX } from "react";
import { Mesh, MeshBasicMaterial, PlaneGeometry } from "three";
import { computeAlignedPose, PlanarPoint } from "./world-map-alignment";
import { useWorldMapPlacement } from "./world-map-renderer";
import { ALIGNMENT_PICKS_COUNT, useWorldMapStore } from "./world-map-store";

/** Minimum distance between the two points picked on the same layer, in meters */
const MIN_PICK_DISTANCE_M = 1;
/** Clicks that moved more than this many pixels are pans, not picks */
const MAX_CLICK_DELTA_PX = 4;
const CATCHER_SIZE_M = 1e6;
const DATA_COLOR = "#00a3e0";
const MAP_COLOR = "#ff9e1b";

type WorldMapAlignmentPickerProps = {
  /** The sheet the map is placed on in the 2D view */
  sheet?: IElementGenericImgSheet;
};

/** @returns The click handling and markers of a running world map alignment in the 2D view */
export function WorldMapAlignmentPicker({
  sheet,
}: WorldMapAlignmentPickerProps): JSX.Element | null {
  const picks = useWorldMapStore((state) => state.alignmentPicks);
  if (!picks) return null;
  return <AlignmentPicker sheet={sheet} picks={picks} />;
}

function AlignmentPicker({
  sheet,
  picks,
}: WorldMapAlignmentPickerProps & { picks: PlanarPoint[] }): JSX.Element {
  const addAlignmentPick = useWorldMapStore((s) => s.addAlignmentPick);
  const setPose = useWorldMapStore((s) => s.setPose);
  const stopAlignment = useWorldMapStore((s) => s.stopAlignment);
  const { pose, height } = useWorldMapPlacement({ sheet });
  const camera = useThree((s) => s.camera);
  const canvas = useThree((s) => s.gl.domElement);

  // An invisible plane right below the camera catches the clicks before sheets and waypoints do
  const catcher = useMemo(
    () =>
      new Mesh(
        new PlaneGeometry(CATCHER_SIZE_M, CATCHER_SIZE_M).rotateX(-Math.PI / 2),
        new MeshBasicMaterial({
          transparent: true,
          opacity: 0,
          depthWrite: false,
        }),
      ),
    [],
  );
  useEffect(
    () => () => {
      catcher.geometry.dispose();
      catcher.material.dispose();
    },
    [catcher],
  );
  useFrame(() => {
    catcher.position.set(
      camera.position.x,
      camera.position.y - camera.near * 2,
      camera.position.z,
    );
  });

  useEffect(() => {
    const previous = canvas.style.cursor;
    canvas.style.cursor = "crosshair";
    return () => {
      canvas.style.cursor = previous;
      // Leaving the 2D view cancels an unfinished alignment
      stopAlignment();
    };
  }, [canvas, stopAlignment]);

  useEffect(() => {
    if (picks.length < ALIGNMENT_PICKS_COUNT) return;
    const [data1, map1, data2, map2] = picks;
    setPose(computeAlignedPose(pose, [data1, data2], [map1, map2]));
    stopAlignment();
  }, [picks, pose, setPose, stopAlignment]);

  function onClick(event: ThreeEvent<MouseEvent>): void {
    event.stopPropagation();
    if (event.delta > MAX_CLICK_DELTA_PX) return;
    const point: PlanarPoint = [event.point.x, event.point.z];
    // The second point of each layer must be far enough from the first one to define a direction
    const sameLayerFirstPick =
      picks.length >= 2 ? picks[picks.length - 2] : undefined;
    if (
      sameLayerFirstPick &&
      Math.hypot(
        point[0] - sameLayerFirstPick[0],
        point[1] - sameLayerFirstPick[1],
      ) < MIN_PICK_DISTANCE_M
    ) {
      return;
    }
    addAlignmentPick(point);
  }

  return (
    <>
      <primitive object={catcher} onClick={onClick} />
      {picks.map(([x, z], index) => (
        <Html
          key={index}
          position={[x, height, z]}
          center
          zIndexRange={[10, 0]}
          style={{ pointerEvents: "none" }}
        >
          <PickMarker
            label={String(Math.floor(index / 2) + 1)}
            color={index % 2 === 0 ? DATA_COLOR : MAP_COLOR}
          />
        </Html>
      ))}
    </>
  );
}

function PickMarker({
  label,
  color,
}: {
  label: string;
  color: string;
}): JSX.Element {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: color,
        border: "2px solid white",
        boxShadow: "0 0 4px rgba(0,0,0,0.8)",
        color: "white",
        font: "bold 11px sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {label}
    </div>
  );
}
